/**
 * EducationInstitutionNew — /education/institutions/new
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Building2, Save, Image as ImageIcon } from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import EducationCategoryNav from "@/components/EducationCategoryNav";
import VideoLinkManager from "@/components/VideoLinkManager";

const empty = {
  name: "", institution_type: "", level: "", category: "",
  description: "", city: "", state: "", country: "", address: "",
  website: "", contact_email: "", contact_phone: "",
  founded_year: "", accreditation: "",
  logo_url: "", hero_image_url: "",
  courses_offered: "", fees_summary: "", admission_summary: "",
  photo_urls: "",
};

export default function EducationInstitutionNew() {
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState(empty);
  const [videoUrls, setVideoUrls] = useState([]);
  const [meta, setMeta] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { http.get("/education/meta").then(r => setMeta(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (!authLoading && !user) {
      toast.error("Sign in to register an institution.");
      nav(`/login?redirect=${encodeURIComponent("/education/institutions/new")}`, { replace: true });
    }
  }, [authLoading, user, nav]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.institution_type) { toast.error("Name and institution type are required."); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        institution_type: form.institution_type,
        level: form.level || null,
        category: form.category || null,
        description: form.description || null,
        city: form.city || null, state: form.state || null, country: form.country || null,
        address: form.address || null,
        website: form.website || null,
        contact_email: form.contact_email || null,
        contact_phone: form.contact_phone || null,
        founded_year: form.founded_year ? Number(form.founded_year) : null,
        accreditation: form.accreditation || null,
        logo_url: form.logo_url || null,
        hero_image_url: form.hero_image_url || null,
        courses_offered: form.courses_offered ? form.courses_offered.split(/[\n,]/).map(s => s.trim()).filter(Boolean) : [],
        fees_summary: form.fees_summary || null,
        admission_summary: form.admission_summary || null,
        photo_urls: form.photo_urls ? form.photo_urls.split(/[\n,]/).map(s => s.trim()).filter(Boolean) : [],
        video_urls: videoUrls,
      };
      const r = await http.post("/education/institutions", payload);
      toast.success("Institution registered!");
      nav(`/education/institutions/${r.data.institution.slug}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create institution.");
    } finally { setSaving(false); }
  };

  return (
    <>
      <EducationCategoryNav />
      <div className="max-w-2xl mx-auto px-4 py-8" data-testid="institution-new-page">
        <div className="overline mb-1">NEW INSTITUTION</div>
        <h1 className="font-display text-4xl mb-2 inline-flex items-center gap-2">
          <Building2 className="w-8 h-8" />Register your institution
        </h1>
        <p className="opacity-70 mb-6">A public page for your school, college, university, library or learning platform.</p>

        <form onSubmit={submit} className="space-y-4">
          <Field label="Name *">
            <input required value={form.name} onChange={e => set("name", e.target.value)} className="wofy-input" data-testid="input-name" />
          </Field>

          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Type *">
              <select required value={form.institution_type} onChange={e => set("institution_type", e.target.value)} className="wofy-input" data-testid="input-type">
                <option value="">Select…</option>
                {meta?.institution_types?.map(t => <option key={t.slug} value={t.slug}>{t.label}</option>)}
              </select>
            </Field>
            <Field label="Level">
              <select value={form.level} onChange={e => set("level", e.target.value)} className="wofy-input" data-testid="input-level">
                <option value="">Any</option>
                {meta?.institution_levels?.map(l => <option key={l.slug} value={l.slug}>{l.label}</option>)}
              </select>
            </Field>
            <Field label="Field / Category">
              <select value={form.category} onChange={e => set("category", e.target.value)} className="wofy-input" data-testid="input-category">
                <option value="">General</option>
                {meta?.institution_categories?.map(c => <option key={c.slug} value={c.slug}>{c.label}</option>)}
              </select>
            </Field>
          </div>

          <Field label="Description">
            <textarea rows={5} value={form.description} onChange={e => set("description", e.target.value)}
              placeholder="Mission, history, what makes you unique…"
              className="wofy-input" data-testid="input-description" />
          </Field>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Founded year">
              <input type="number" min="1500" max="2099" value={form.founded_year} onChange={e => set("founded_year", e.target.value)} className="wofy-input" data-testid="input-founded" />
            </Field>
            <Field label="Accreditation">
              <input value={form.accreditation} onChange={e => set("accreditation", e.target.value)} className="wofy-input" data-testid="input-accreditation" />
            </Field>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="City"><input value={form.city} onChange={e => set("city", e.target.value)} className="wofy-input" data-testid="input-city" /></Field>
            <Field label="State"><input value={form.state} onChange={e => set("state", e.target.value)} className="wofy-input" data-testid="input-state" /></Field>
            <Field label="Country"><input value={form.country} onChange={e => set("country", e.target.value)} className="wofy-input" data-testid="input-country" /></Field>
          </div>
          <Field label="Address"><input value={form.address} onChange={e => set("address", e.target.value)} className="wofy-input" data-testid="input-address" /></Field>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Logo URL"><input value={form.logo_url} onChange={e => set("logo_url", e.target.value)} className="wofy-input" data-testid="input-logo" /></Field>
            <Field label="Hero image URL"><input value={form.hero_image_url} onChange={e => set("hero_image_url", e.target.value)} className="wofy-input" data-testid="input-hero" /></Field>
          </div>

          <Field label="Courses offered (comma-separated or one per line)">
            <textarea rows={3} value={form.courses_offered} onChange={e => set("courses_offered", e.target.value)}
              placeholder="MBBS, BE Computer Science, B.Com…" className="wofy-input" data-testid="input-courses" />
          </Field>

          <Field label="Fees summary">
            <textarea rows={2} value={form.fees_summary} onChange={e => set("fees_summary", e.target.value)} className="wofy-input" data-testid="input-fees" />
          </Field>
          <Field label="Admission summary">
            <textarea rows={3} value={form.admission_summary} onChange={e => set("admission_summary", e.target.value)}
              placeholder="Requirements, deadlines, entrance exams…" className="wofy-input" data-testid="input-admission" />
          </Field>

          {/* Photos + Videos, side-by-side */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <div className="text-[11px] font-mono uppercase tracking-wider opacity-70 mb-2 inline-flex items-center gap-1">
                <ImageIcon className="w-3.5 h-3.5" /> Upload photos (URLs, one per line)
              </div>
              <textarea rows={4} value={form.photo_urls} onChange={e => set("photo_urls", e.target.value)}
                placeholder="https://…jpg" className="wofy-input font-mono text-xs" data-testid="input-photos" />
              <p className="text-xs opacity-60 mt-1">Up to 24 photos.</p>
            </div>
            <div>
              <VideoLinkManager value={videoUrls} onChange={setVideoUrls} max={8}
                helper="Paste YouTube or Vimeo links (campus tour, faculty intro, student stories)." />
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Website"><input value={form.website} onChange={e => set("website", e.target.value)} className="wofy-input" data-testid="input-website" /></Field>
            <Field label="Contact email"><input type="email" value={form.contact_email} onChange={e => set("contact_email", e.target.value)} className="wofy-input" data-testid="input-email" /></Field>
            <Field label="Contact phone"><input value={form.contact_phone} onChange={e => set("contact_phone", e.target.value)} className="wofy-input" data-testid="input-phone" /></Field>
          </div>

          <div className="pt-2">
            <button disabled={saving} className="wofy-btn-primary inline-flex items-center gap-1.5 disabled:opacity-40" data-testid="submit-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Register institution
            </button>
          </div>
        </form>
      </div>
    </>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[11px] font-mono uppercase tracking-wider opacity-70 mb-1">{label}</div>
      {children}
    </label>
  );
}
